<template>
  <a href="#" target="" class="btn font-weight-bold m-btn--pill btn-block d-flex justify-content-center"
     :class="[`btn-outline-${variant}`]" @click="openAuth">
    <div class="ml-3 mr-4">
      <spinner v-if="isLoading" class="g-spinner trans"></spinner>
      <img v-if="isGoogle" src="@/assets/google.svg" class="icon" :class="[isLoading && 'invisible']" alt="">
      <img v-if="isFacebook" src="@/assets/btn_fb.png" class="icon" :class="[isLoading && 'invisible']" alt="">
      <img v-if="isApple || isAppleV2" src="@/assets/Apple_logo_black.svg" class="icon" :class="[isLoading && 'invisible']" alt="">
    </div>
    <span class="mr-3 continue-text">{{ $t('Continue with') }} {{ variant.charAt(0).toUpperCase() + variant.slice(1).replace(/[0-9]/g, '') }}</span>
  </a>
</template>

<script>
import {mapActions, mapState} from "vuex";
import Spinner from "../spinner/Spinner";
import {isPwa,isIosApp,isAndroidApp} from "@/utils/Pwa";
import {storePostLoginRedirect, SOCIAL_LOGIN_STORAGE_KEY} from "@/utils/safePath";
import {trackLogin} from "@/utils/ConversionAnalytics";

export default {
  name: "SocialSignInButton",
  components: {
    Spinner,
  },
  props: {
    variant: String
  },
  data() {
    return {
      isLoading: false,
      isFocusListener: false,
      closeFallbackTimer: null,
    }
  },
  created() {
    window.addEventListener('message', this.onMessage);
    if (this.$route.query.type) {
      if(this.$route.query.saveStateAndClose){
        localStorage.setItem(SOCIAL_LOGIN_STORAGE_KEY, JSON.stringify({
          'code': this.$route.query.code,
          'user': this.$route.query.user,
          'type': this.$route.query.type,
          'redirect': this.$route.query.redirect || ''
        }));
        window.close();
        // Android Chrome/TWA may keep the callback tab open after a
        // cross-origin OAuth round-trip. If closing worked this timer is
        // destroyed with the page; otherwise finish login in the current tab.
        this.closeFallbackTimer = window.setTimeout(() => {
          this.closeFallbackTimer = null;
          this.socialLoginEvenListener();
        }, 250);
      }else{
        storePostLoginRedirect(this.$route.query.redirect);
        this.loginCall(this.$route.query.type, this.$route.query.code, this.$route.query.user)
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
    if (this.closeFallbackTimer !== null) {
      window.clearTimeout(this.closeFallbackTimer);
      this.closeFallbackTimer = null;
    }
    this.removeFocusEventListener();
  },
  methods: {
    ...mapActions([
      'afterLoginSuccess',
      'setLoginError',
      'openRegistrationModal'
    ]),
    openAuth() {
      this.isLoading = true;
      this.addFocusEventListener();
      const popup = window.open(this.authHref, 'Logowanie', 'width=600,height=600');
        const timer = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(timer);
            this.isLoading = false;
          }
        }, 1000);
    },

    loginCall(type, code,user = '') {
      if (type === `${this.variant}Auth`) {
        this.isLoading = true;
        this.$http.post(`login/${this.variant}`, {
          token: code,
          user: user,
          entityUuid: this.entityUuid,
          pwa: isPwa,
          android: isAndroidApp,
          ios: isIosApp
        })
            .then((r) => {
              if (r.data.success && r.data.showRegistrationModal) {
                this.openRegistrationModal(r.data.registrationToken);
                return;
              }

              if (r.data.success) {
                trackLogin(this.variant);
                this.afterLoginSuccess({
                  ...r.data,
                  entity: this.entityUuid,
                })
              } else {
                this.setLoginError(r.data.message);
              }
            })
        this.isLoading = false;
      }
    },
    socialLoginEvenListener(){
      let storageItem = localStorage.getItem(SOCIAL_LOGIN_STORAGE_KEY);
      if(storageItem){
        try{
          let jsonStorage = JSON.parse(storageItem);
          if(jsonStorage && jsonStorage.type && jsonStorage.type === `${this.variant}Auth`){
            localStorage.removeItem(SOCIAL_LOGIN_STORAGE_KEY);
            if(jsonStorage.type && jsonStorage.code){
              storePostLoginRedirect(jsonStorage.redirect);
              this.loginCall(jsonStorage.type, jsonStorage.code, jsonStorage.user||null)
            }
          }
        }catch (e){
          localStorage.removeItem(SOCIAL_LOGIN_STORAGE_KEY);
        }
      }
    },
    addFocusEventListener() {
      if (!this.isFocusListener) {
        window.addEventListener("focus", this.socialLoginEvenListener);
        this.isFocusListener = true;
      }
    },
    removeFocusEventListener() {
      if (this.isFocusListener) {
        localStorage.removeItem(SOCIAL_LOGIN_STORAGE_KEY);
        window.removeEventListener("focus", this.socialLoginEvenListener);
        this.isFocusListener = false;
      }
    },
    onMessage(message) {
      if(message.data.type){
        storePostLoginRedirect(message.data.redirect);
        this.loginCall(message.data.type,message.data.code,message.data.user);
      }
    },
  },
  computed: {
    ...mapState({
      entityUuid: state => state.auth.entity.uuid,
      appScheme: state => state.settings.app.apple.scheme,
    }),
    authHref() {
      // APPLE_APP_SCHEME belongs to the native iOS wrapper. Android MK1 is a
      // Trusted Web Activity and returns through its verified HTTPS App Link.
      // Passing the iOS scheme from an Android TWA leaves the OAuth callback in
      // the browser because the APK does not register that custom scheme.
      const scheme = isIosApp && this.appScheme ? this.appScheme : 'https'
      let href = `${window.authUrl}providers/${this.variant}/?url=${location.host}&scheme=${encodeURIComponent(scheme)}`
      const redirect = this.$route.query.redirect
      if (typeof redirect === 'string' && redirect.length > 0) {
        href += `&redirect=${encodeURIComponent(redirect)}`
      }
      return href
    },
    isGoogle() {
      return this.variant === 'google';
    },
    isFacebook() {
      return this.variant === 'facebook';
    },
    isApple() {
      return this.variant === 'apple';
    },
    isAppleV2() {
      return this.variant === 'apple2';
    },
  }
}
</script>

<style scoped>
.icon {
  width: 18px;
  height: 18px;
}

.btn {
  font-weight: 400 !important;
  text-align: left;
}

.btn-outline-google {
  border-color: #4e86ec;
}

.btn-outline-google:hover {
  background: rgba(78, 134, 236, 0.1);
}

.btn-outline-facebook {
  border-color: #4267B2;
}

.btn-outline-facebook:hover {
  background: rgba(66, 103, 178, 0.1);
}

.btn-outline-apple,.btn-outline-apple2 {
  border-color: #000000;
}

.btn-outline-apple:hover,.btn-outline-apple2:hover {
  background: rgba(0, 0, 0, 0.1);
}

.g-spinner {
  width: 18px !important;
  height: 18px !important;
  position: absolute;
}

.continue-text {
  min-width: 141px;
}
</style>
